import { useRef, useState, useEffect } from 'react';
import Webcam from 'react-webcam';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Video as VideoIcon, AlertCircle } from 'lucide-react';
import { useApi } from '../services/api'; // FIXED: Import useApi hook for authenticated API
import { useTheme } from '../stores/useTheme'; // Filled store

// Define the props interface (now requires applicationId for real flow)
interface VideoTestProps {
  applicationId: string; // NEW: Required prop for real ID
  question: {
    id: string;
    text: string;
    timeLimit: number;
  };
  onUploadSuccess: (url: string) => void;
}

const VideoTest: React.FC<VideoTestProps> = ({ applicationId, question, onUploadSuccess }) => {
  const api = useApi(); // FIXED: Use the hook to get authenticated api instance
  const webcamRef = useRef<Webcam>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]); // Use ref for synchronous chunk collection
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [recording, setRecording] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(question.timeLimit);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [responseId, setResponseId] = useState<string | null>(null); // Dynamic ID
  const [interviewId, setInterviewId] = useState<string | null>(null); // New: Dynamic interview ID
  const [sessionLocked, setSessionLocked] = useState(false); // NEW: For continuity
  const theme = useTheme((state) => state.theme); // NEW: Dynamic theme

  const videoConstraints = {
    width: { ideal: 1280 },
    height: { ideal: 720 },
    facingMode: 'user',
  }; // 720p quality controls

  // Fetch/create interviewId then responseId on component mount (use real applicationId prop)
  useEffect(() => {
    const startSession = async () => {
      try {
        // Step 1: Create Interview with real applicationId
        const interviewRes = await api.post('/interviews', { applicationId });
        const newInterviewId = interviewRes.data.id; // Assume response has 'id' for Interview
        setInterviewId(newInterviewId);
        console.log('Created interview ID:', newInterviewId); // Debug

        // Step 2: Create Response with interviewId
        const responseRes = await api.post('/responses', {
          interviewId: newInterviewId,
          questionId: question.id,
        });
        setResponseId(responseRes.data.id); // Assume response has 'id' for Response
        console.log('Created response ID:', responseRes.data.id); // Debug
      } catch (err) {
        console.error('Failed to start session:', err);
        setError('Failed to start interview session.');
      }
    };
    startSession();
  }, [applicationId, question.id, api]); // Add api to deps

  // Check permissions on mount
  useEffect(() => {
    navigator.mediaDevices.getUserMedia({ video: true, audio: true })
      .then(() => setPermissionGranted(true))
      .catch(() => setError('Camera and microphone permissions required.'));
  }, []);

  // Timer for time limit
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (recording && timeLeft > 0) {
      timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
    } else if (timeLeft === 0 && recording) {
      handleStopRecording();
    }
    return () => clearTimeout(timer);
  }, [recording, timeLeft]);

  const handleStartRecording = () => {
    if (!responseId || sessionLocked) {
      setError(sessionLocked ? 'Session locked: No restarts allowed.' : 'Session not ready. Try again.');
      return;
    }
    setSessionLocked(true); // Lock on start to enforce no restarts
    setRecording(true);
    setTimeLeft(question.timeLimit);
    setVideoUrl(null);
    const stream = webcamRef.current?.stream;
    if (stream) {
      chunksRef.current = []; // Reset chunks at start
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'video/webm' });
      mediaRecorderRef.current.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
          console.log('Chunk added, total chunks:', chunksRef.current.length);
        }
      });
      mediaRecorderRef.current.addEventListener('stop', () => {
        console.log('onstop fired, local chunks length:', chunksRef.current.length);
        if (chunksRef.current.length > 0) {
          const blob = new Blob(chunksRef.current, { type: 'video/webm' });
          setVideoUrl(URL.createObjectURL(blob)); // Set local URL for preview
          handleUpload(blob); // Auto-trigger upload with blob
          chunksRef.current = []; // Clear after use
        } else {
          console.error('No chunks available on stop');
        }
      });
      mediaRecorderRef.current.start();
      console.log('Recording started');
    } else {
      console.error('No stream available');
    }
  };

  const handleStopRecording = () => {
    setRecording(false);
    mediaRecorderRef.current?.stop();
    console.log('Stop requested'); // Debug: Confirm stop call
  };

  const handleUpload = async (blob: Blob) => {
    console.log('Upload starting'); // Debug: Before post
    setUploading(true);
    const formData = new FormData();
    formData.append('video', blob, `${question.id}.webm`);
    formData.append('responseId', responseId || ''); // Use dynamic ID
    try {
      const response = await api.post('/recording/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      console.log('Upload success, response:', response.data); // Debug: On success
      const { videoUrl: serverVideoUrl } = response.data;
      console.log('Server video URL (for debug; not used for playback in stub):', serverVideoUrl); // Log but don't set
      onUploadSuccess(serverVideoUrl);
    } catch (err) {
      setError('Upload failed. Check console for details.');
      console.error('Upload error:', err); // Debug: On fail
    } finally {
      setUploading(false);
      console.log('Upload complete (success or fail)'); // Debug: Always
    }
  };

  return (
    <div className="bg-surface-card p-6 rounded-input max-w-xl mx-auto">
      <h3 className="text-xl font-semibold mb-4">{question.text}</h3>
      {!permissionGranted && <p className="text-red-400 mb-4">{error}</p>}
      {permissionGranted && (
        <>
          {recording && (
            <p className="text-txt-muted mb-2">Time left: {timeLeft} seconds</p>
          )}
          <Webcam
            audio={true}
            ref={webcamRef}
            videoConstraints={videoConstraints}
            className="w-full h-auto rounded mb-4"
            key="webcam" // Unique key
          />
          {!recording ? (
            <button onClick={handleStartRecording} className="bg-brand-600 hover:bg-brand-500 text-txt-primary px-6 py-2 rounded-input font-medium transition-colors">
              Start Recording
            </button>
          ) : (
            <button onClick={handleStopRecording} className="bg-red-600 hover:bg-red-500 text-txt-primary px-6 py-2 rounded-input font-medium transition-colors">
              Stop Recording
            </button>
          )}
          {uploading && <p className="text-txt-muted mt-4">Uploading video...</p>}
          {videoUrl && (
            <video src={videoUrl} controls className="w-full h-auto rounded mt-4" />
          )}
          {error && (
            <div className="flex items-center text-red-400 mt-4">
              <AlertCircle className="mr-2" />
              {error}
              <button onClick={() => setError(null)} className="ml-4 bg-[var(--primary)] text-txt-primary px-4 py-2 rounded" aria-label="Close error">
                Close
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VideoTest;